import { AluuCurated, Category } from '@apis/index'
import {
  deletesvg,
  edit,
  outlinestore,
  switchOff,
  switchOn,
} from '@assets/icons'
import Column from '@components/column'
import Svg from '@components/svg'
import Table from '@components/table'
import Title from '@components/title'
import { useModal } from '@context/ModalContext'
import { RootState } from '@store/index'
import imageCompression from 'browser-image-compression'
import React, { Fragment, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  BalanceCard,
  BalanceLabel,
  BalanceValue,
  ChangeGameLink,
  Chip,
  ChipImage,
  ChipList,
  EditableCell,
  EditableInput,
  EditableSaveButton,
  EmptyState,
  GameCard,
  GameCardImage,
  GameCardName,
  GameGrid,
  HiddenFileInput,
  PickerSearchInput,
  PickerWrapper,
  RowEdit,
  RowThumb,
  RowThumbPlaceholder,
  SelectedGameBar,
  SelectedGameImage,
  SelectedGameText,
} from './style'

const gameImageCache = new Map<string, string>()

const GameImage = ({
  gamecode,
  alt,
  ImageComponent,
}: {
  gamecode: string
  alt: string
  ImageComponent: typeof GameCardImage
}) => {
  const [src, setSrc] = useState(gameImageCache.get(gamecode) || '')

  React.useEffect(() => {
    if (gameImageCache.has(gamecode)) {
      setSrc(gameImageCache.get(gamecode) as string)
      return
    }
    let cancelled = false
    const fetchImage = async () => {
      try {
        const response = await AluuCurated.gameImage({ id: gamecode })
        if (!cancelled && response) {
          gameImageCache.set(gamecode, response)
          setSrc(response)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchImage()
    return () => {
      cancelled = true
    }
  }, [gamecode])

  if (!src) return null

  return <ImageComponent src={src} alt={alt} />
}

const AluuCataloguePicker = ({
  gameCode,
  onAdd,
}: {
  gameCode: string
  onAdd: (denom: string) => void
}) => {
  const [catalogue, setCatalogue] = useState<any[]>([])
  const [catalogueLoading, setCatalogueLoading] = useState(true)

  React.useEffect(() => {
    const fetchCatalogue = async () => {
      try {
        const response = await AluuCurated.catalogue({ id: gameCode })
        setCatalogue(response || [])
      } catch (error) {
        console.error(error)
      } finally {
        setCatalogueLoading(false)
      }
    }
    fetchCatalogue()
  }, [gameCode])

  if (catalogueLoading) {
    return <div>Loading...</div>
  }

  return (
    <Table
      data={catalogue}
      columns={[
        { title: 'Pack', key: 'Pack' },
        { title: 'Name', key: 'name' },
        { title: 'Price', key: 'price' },
        { title: 'Stock', key: 'stockStatus' },
      ]}
      searchKeys={['name']}
      hasSearchbar
      clientSearch
      text="Live product catalogue"
      icon={outlinestore}
      actions={[
        {
          key: 'add',
          icon: outlinestore,
          onClick: (item) => onAdd(item.Pack),
        },
      ]}
    />
  )
}

const resolveImageSrc = (image?: string | null) => {
  if (!image) return ''
  return image.startsWith('http')
    ? image
    : `https://admin.tournaop.com/${image}`
}

// Product-creation picker restricted to categories created on the main Category page.
const GamePicker = ({
  games,
  onSelect,
}: {
  games: { gameCode: string; gameName: string; image?: string | null }[]
  onSelect: (gameCode: string) => void
}) => {
  const [search, setSearch] = useState('')

  const filteredGames = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return games
    return games.filter((game) => game.gameName.toLowerCase().includes(term))
  }, [games, search])

  return (
    <PickerWrapper>
      <PickerSearchInput
        type="text"
        placeholder="Search games..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <GameGrid>
        {filteredGames.map((game) => (
          <GameCard
            key={game.gameCode}
            type="button"
            onClick={() => onSelect(game.gameCode)}
          >
            {game.image ? (
              <GameCardImage
                src={resolveImageSrc(game.image)}
                alt={game.gameName}
              />
            ) : (
              <GameImage
                gamecode={game.gameCode}
                alt={game.gameName}
                ImageComponent={GameCardImage}
              />
            )}
            <GameCardName>{game.gameName}</GameCardName>
          </GameCard>
        ))}
      </GameGrid>
    </PickerWrapper>
  )
}

interface SelectedGame {
  id: number
  gameCode: string
  gameName: string
  image?: string | null
}

const EditablePriceCell = ({
  item,
  onSave,
}: {
  item: {
    id: number
    price: number
    customPrice: number | null
    effectivePrice: number
  }
  onSave: (id: number, customPrice: number | null) => Promise<void>
}) => {
  const [value, setValue] = useState(
    item.customPrice !== null ? String(item.customPrice) : '',
  )
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    const trimmed = value.trim()
    const parsed = trimmed === '' ? null : Number(trimmed)
    if (trimmed !== '' && (Number.isNaN(parsed) || (parsed as number) < 0))
      return
    setSaving(true)
    try {
      await onSave(item.id, parsed)
    } finally {
      setSaving(false)
    }
  }

  return (
    <EditableCell>
      <EditableInput
        type="number"
        min={0}
        step="0.01"
        placeholder={String(item.price)}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleSave()}
      />
      <EditableSaveButton type="button" disabled={saving} onClick={handleSave}>
        {saving ? '...' : '✓'}
      </EditableSaveButton>
    </EditableCell>
  )
}

const ProductImageCell = ({
  item,
  onUploadImage,
}: {
  item: { id: number; image: string | null; name: string }
  onUploadImage: (id: number, file: File) => void
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <EditableCell>
      {item.image ? (
        <RowThumb src={resolveImageSrc(item.image)} alt={item.name} />
      ) : (
        <RowThumbPlaceholder>N/A</RowThumbPlaceholder>
      )}
      <RowEdit
        onClick={(e) => {
          e.stopPropagation()
          fileInputRef.current?.click()
        }}
      >
        <Svg src={edit} />
      </RowEdit>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onUploadImage(item.id, file)
          e.target.value = ''
        }}
      />
    </EditableCell>
  )
}

const GameTopUps = () => {
  const {
    items: products,
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.aluuCuratedProducts)
  const [page, setPage] = useState(1)
  const [selectedGames, setSelectedGames] = useState<SelectedGame[]>([])
  const [selectedGame, setSelectedGame] = useState<SelectedGame | null>(null)
  const [filterGameCode, setFilterGameCode] = useState('')
  const [balance, setBalance] = useState<{
    wallet_balance: number
    currency: string
  } | null>(null)
  const [balanceLoading, setBalanceLoading] = useState(true)
  const { showModal, hideModal } = useModal()

  const data = products
    .filter((product) => !filterGameCode || product.gameCode === filterGameCode)
    .map((product) => ({
      id: product.id,
      gameCode: product.gameCode,
      denom: product.denom,
      name: product.name,
      price: product.price,
      customPrice: product.customPrice ?? null,
      effectivePrice: product.effectivePrice ?? product.price,
      image: product.image ?? null,
      isEnabled: product.isEnabled,
    }))

  const saveCustomPrice = async (id: number, customPrice: number | null) => {
    try {
      await AluuCurated.update({
        state: 'aluuCuratedProducts',
        id,
        data: { customPrice },
      })
    } catch (error) {
      console.error(error)
    }
  }

  const uploadProductImage = async (id: number, file: File) => {
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      })
      const reader = new FileReader()
      reader.onload = async (e) => {
        const result = e.target?.result as string
        const base64 = result.split(',')[1]
        const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg'
        try {
          await AluuCurated.update({
            state: 'aluuCuratedProducts',
            id,
            data: { image: { base64, extension } },
          })
        } catch (error) {
          console.error(error)
        }
      }
      reader.readAsDataURL(compressed)
    } catch (error) {
      console.error(error)
    }
  }

  const columns = [
    { title: 'Game', key: 'gameCode' },
    { title: 'Denom', key: 'denom' },
    { title: 'Name', key: 'name' },
    {
      title: 'Image',
      key: 'image',
      render: (item) => (
        <ProductImageCell item={item} onUploadImage={uploadProductImage} />
      ),
    },
    { title: 'Default Price', key: 'price' },
    {
      title: 'Custom Price',
      key: 'customPrice',
      render: (item) => (
        <EditablePriceCell item={item} onSave={saveCustomPrice} />
      ),
    },
    {
      title: 'Status',
      key: 'isEnabled',
      render: (item) => (
        <Svg
          src={item.isEnabled ? switchOn : switchOff}
          onClick={async () =>
            await AluuCurated.update({
              state: 'aluuCuratedProducts',
              id: item.id,
              data: { isEnabled: !item.isEnabled },
            })
          }
        />
      ),
    },
  ]

  const fetchProducts = async () => {
    try {
      await AluuCurated.list({
        state: 'aluuCuratedProducts',
        params: { page, limit: 30 },
      })
    } catch (error) {
      console.error(error)
    }
  }

  const fetchBalance = async () => {
    try {
      const response = await AluuCurated.balance()
      setBalance(response || null)
    } catch (error) {
      console.error(error)
    } finally {
      setBalanceLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await Category.list({ params: { type: 'aluu' } })
      setSelectedGames(
        (response?.data || response || []).map((c: any) => ({
          id: c.id,
          gameCode: c.key,
          gameName: c.name,
          image: c.image ?? null,
        })),
      )
    } catch (error) {
      console.error(error)
    }
  }

  const addProduct = async (gameCode: string, denom: string) => {
    try {
      await AluuCurated.create({
        state: 'aluuCuratedProducts',
        data: { gameCode, denom },
      })
      hideModal()
    } catch (error) {
      console.error(error)
    }
  }

  const openPicker = (gameCode: string) => {
    showModal(
      <AluuCataloguePicker
        gameCode={gameCode}
        onAdd={(denom) => addProduct(gameCode, denom)}
      />,
      { width: 1000 },
    )
  }

  const handleSelectGame = (gameCode: string) => {
    const game = selectedGames.find((g) => g.gameCode === gameCode) || null
    setSelectedGame(game)
    if (game) {
      openPicker(game.gameCode)
    }
  }

  React.useEffect(() => {
    fetchProducts()
  }, [page])

  React.useEffect(() => {
    fetchCategories()
    fetchBalance()
  }, [])

  return (
    <Fragment>
      <Column>
        <Title textone="Store Management >" texttwo="Game Top-Ups" />

        <BalanceCard>
          <BalanceLabel>Aluu Wallet Balance</BalanceLabel>
          <BalanceValue>
            {balanceLoading
              ? 'Loading...'
              : balance
                ? `${balance.currency} ${Number(balance.wallet_balance).toFixed(2)}`
                : 'Unavailable'}
          </BalanceValue>
        </BalanceCard>

        {selectedGame ? (
          <SelectedGameBar>
            <GameImage
              gamecode={selectedGame.gameCode}
              alt={selectedGame.gameName}
              ImageComponent={SelectedGameImage}
            />
            <SelectedGameText>{selectedGame.gameName}</SelectedGameText>
            <ChangeGameLink onClick={() => openPicker(selectedGame.gameCode)}>
              Add another product
            </ChangeGameLink>
            <ChangeGameLink onClick={() => setSelectedGame(null)}>
              Change game
            </ChangeGameLink>
          </SelectedGameBar>
        ) : selectedGames.length === 0 ? (
          <EmptyState>
            No AllU categories yet. Create one on the Category page first (Type:
            AllU).
          </EmptyState>
        ) : (
          <GamePicker games={selectedGames} onSelect={handleSelectGame} />
        )}

        <Table
          onPageChange={setPage}
          totalItems={totalItems}
          totalPages={totalPages}
          pageControlled={page}
          itemsPerPage={30}
          data={data}
          columns={columns}
          searchKeys={['name', 'gameCode', 'denom']}
          text="Curated top-up products"
          icon={outlinestore}
          actions={[
            {
              key: 'delete',
              icon: deletesvg,
              onClick: async (item) =>
                await AluuCurated.remove({
                  id: item.id,
                  state: 'aluuCuratedProducts',
                }),
            },
          ]}
        />
      </Column>
    </Fragment>
  )
}

export default GameTopUps
